/**
 * @file Tree 树形列表
 * @description 可展开节点；子级前带竖向引导线；支持多选勾选与单选高亮。
 *
 * @whenToUse 目录、组织架构、权限树。
 * @whenNotToUse 下拉选树节点（用 TreeSelect）。
 */

import { useState, type ReactNode } from 'react'
import { ChevronRight } from 'lucide-react'
import { cn } from '@/lib/cn.ts'
import { Checkbox } from '@/components/ui/checkbox.tsx'

export type TreeNode = {
  key: string
  title: ReactNode
  children?: TreeNode[]
  disabled?: boolean
}

export type TreeProps = {
  treeData: TreeNode[]
  /** 展开的 key */
  expandedKeys?: string[]
  onExpand?: (keys: string[]) => void
  /** 默认展开 */
  defaultExpandedKeys?: string[]
  /** 勾选的 key（多选） */
  checkedKeys?: string[]
  onCheck?: (keys: string[]) => void
  /** 是否显示勾选 */
  checkable?: boolean
  /** 高亮选中的 key（单选回显，如 TreeSelect） */
  selectedKeys?: string[]
  /** 点击节点标题/行 */
  onSelect?: (key: string, node: TreeNode) => void
  /** 仅叶子节点可选（有 children 的只展开） */
  selectableLeafOnly?: boolean
  className?: string
}

/** 与展开按钮 size-6 对齐，引导线画在槽位中线 */
const GUIDE_SLOT = 24

function collectChildKeys(node: TreeNode): string[] {
  const keys: string[] = []
  const walk = (n: TreeNode) => {
    keys.push(n.key)
    n.children?.forEach(walk)
  }
  walk(node)
  return keys
}

/** 引导线竖向槽位中线距行左侧的水平偏移（px-1 + 槽位中心） */
function guideLineLeft(depth: number): string {
  return `calc(0.25rem + ${(depth - 1) * GUIDE_SLOT + GUIDE_SLOT / 2}px)`
}

/**
 * 子节点前的竖向/横向引导线（末项为 L，其余为 T，并延续未结束的祖先竖线）。
 * @param ancestorLast 路径上各层祖先是否为同级末项
 * @param isLast 当前节点是否为同级末项
 */
function renderTreeGuides(
  ancestorLast: boolean[],
  isLast: boolean,
): ReactNode {
  const depth = ancestorLast.length
  if (depth === 0) return null

  return (
    <span
      className="relative h-8 shrink-0"
      style={{ width: depth * GUIDE_SLOT }}
      aria-hidden
    >
      {ancestorLast.map((last, level) =>
        last ? null : (
          <span
            key={level}
            className="absolute inset-y-0 w-px bg-border"
            style={{ left: level * GUIDE_SLOT + GUIDE_SLOT / 2 }}
          />
        ),
      )}
      <span
        className={cn(
          'absolute w-px bg-border',
          isLast ? 'top-0 h-4' : 'inset-y-0',
        )}
        style={{ left: (depth - 1) * GUIDE_SLOT + GUIDE_SLOT / 2 }}
      />
      <span
        className="absolute top-4 h-px bg-border"
        style={{
          left: (depth - 1) * GUIDE_SLOT + GUIDE_SLOT / 2,
          width: GUIDE_SLOT / 2,
        }}
      />
    </span>
  )
}

/**
 * 非末项节点在 li 层级的竖线延续，贯穿已展开的子树，避免三级及以上出现断层。
 * @param depth 当前节点深度（ancestorLast.length）
 */
function renderTreeGuideContinuation(depth: number): ReactNode {
  if (depth === 0) return null

  return (
    <span
      className="pointer-events-none absolute bottom-0 top-4 w-px bg-border"
      style={{ left: guideLineLeft(depth) }}
      aria-hidden
    />
  )
}

export function Tree({
  treeData,
  expandedKeys,
  onExpand,
  defaultExpandedKeys = [],
  checkedKeys = [],
  onCheck,
  checkable = false,
  selectedKeys = [],
  onSelect,
  selectableLeafOnly = false,
  className,
}: TreeProps) {
  const [innerExpanded, setInnerExpanded] = useState(defaultExpandedKeys)
  const expanded = expandedKeys ?? innerExpanded

  const setExpanded = (keys: string[]) => {
    onExpand?.(keys)
    if (expandedKeys === undefined) setInnerExpanded(keys)
  }

  const toggleExpand = (key: string) => {
    setExpanded(
      expanded.includes(key)
        ? expanded.filter((k) => k !== key)
        : [...expanded, key],
    )
  }

  const toggleCheck = (node: TreeNode, checked: boolean) => {
    const related = collectChildKeys(node)
    if (checked) {
      onCheck?.([...new Set([...checkedKeys, ...related])])
      return
    }
    onCheck?.(checkedKeys.filter((k) => !related.includes(k)))
  }

  const handleSelect = (node: TreeNode, hasChildren: boolean) => {
    if (node.disabled) return
    if (selectableLeafOnly && hasChildren) return
    onSelect?.(node.key, node)
  }

  const renderNodes = (nodes: TreeNode[], ancestorLast: boolean[]) =>
    nodes.map((node, index) => {
      const hasChildren = Boolean(node.children?.length)
      const isLast = index === nodes.length - 1
      const isExpanded = expanded.includes(node.key)
      const isChecked = checkedKeys.includes(node.key)
      const isSelected = selectedKeys.includes(node.key)
      const canSelect =
        Boolean(onSelect) &&
        !node.disabled &&
        !(selectableLeafOnly && hasChildren)

      const depth = ancestorLast.length

      return (
        <li key={node.key} className="relative select-none">
          {!isLast ? renderTreeGuideContinuation(depth) : null}
          <div
            role={canSelect ? 'treeitem' : undefined}
            aria-selected={canSelect ? isSelected : undefined}
            className={cn(
              'flex h-8 items-center rounded-md px-1 text-sm transition-all',
              canSelect && 'cursor-pointer hover:opacity-90',
              isSelected && 'bg-primary/15 text-primary',
              !isSelected && 'text-foreground hover:bg-foreground/5',
              node.disabled && 'opacity-50',
            )}
            onClick={() => {
              if (canSelect) handleSelect(node, hasChildren)
            }}
          >
            {renderTreeGuides(ancestorLast, isLast)}
            <div className="flex min-w-0 flex-1 items-center gap-1">
              {hasChildren ? (
                <button
                  type="button"
                  aria-label={isExpanded ? '收起' : '展开'}
                  className="inline-flex size-6 items-center justify-center rounded-md text-muted hover:bg-foreground/10 transition-all duration-300"
                  onClick={(event) => {
                    event.stopPropagation()
                    toggleExpand(node.key)
                  }}
                >
                  <ChevronRight
                    className={cn(
                      'size-4 transition-transform duration-200',
                      isExpanded && 'rotate-90',
                    )}
                    aria-hidden
                  />
                </button>
              ) : (
                <span className="inline-flex size-2" aria-hidden />
              )}
              {checkable ? (
                <Checkbox
                  checked={isChecked}
                  disabled={node.disabled}
                  aria-label={typeof node.title === 'string' ? node.title : '选择'}
                  onChange={(event) => toggleCheck(node, event.target.checked)}
                  onClick={(event) => event.stopPropagation()}
                />
              ) : null}
              <span
                className={cn(
                  'min-w-0 flex-1',
                  typeof node.title === 'string' && 'truncate',
                )}
              >
                {node.title}
              </span>
            </div>
          </div>
          {hasChildren && isExpanded ? (
            <ul>
              {renderNodes(node.children!, [...ancestorLast, isLast])}
            </ul>
          ) : null}
        </li>
      )
    })

  return (
    <ul className={cn('text-sm', className)} role="tree">
      {renderNodes(treeData, [])}
    </ul>
  )
}
